import { useQuery } from '@tanstack/react-query';
import { organizationsApi } from '../services/api';
import { useAuthStore } from '../services/auth';

/**
 * Returns the organization's configured timezone (IANA string).
 * Falls back to 'UTC' if not set or not loaded yet.
 */
export function useTimezone(): string {
  const organizationId = useAuthStore((s) => s.organizationId);

  const { data: organization } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  return organization?.timezone || 'UTC';
}

/**
 * Format a UTC date string using the given IANA timezone.
 */
export function formatInTimezone(
  dateStr: string | undefined | null,
  timezone: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateStr) return 'Never';
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      timeZone: timezone,
      ...options,
    });
  } catch {
    return new Date(dateStr).toLocaleString();
  }
}

/**
 * Format a UTC date string as time-only using the given IANA timezone.
 */
export function formatTimeInTimezone(
  dateStr: string | undefined | null,
  timezone: string,
): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString(undefined, {
      timeZone: timezone,
    });
  } catch {
    return new Date(dateStr).toLocaleTimeString();
  }
}
